import { useEffect, useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore, type KeyboardEvent, type MouseEvent, type ReactElement } from "react";
import type { BagRefusalReason, BagSortOrder, Rules } from "odyssey-shared";
import { EmptyState, Glyph, ITEM_SLOT_ART_BOX, ItemSlot, KitInputProvider, PendingSpinner, PlayWindow, logical, type KitInput, type PlaceSource, type SizeSource } from "../../ui/kit/index.ts";
import { itemArt, useIcons } from "../icons/context.tsx";
import type { TipController } from "../bar/tips.ts";
import { useTipHandlers } from "../bar/tip-handlers.ts";
import type { HudArea } from "../hud/slots.ts";
import { DragGhost } from "../drag/ghost.tsx";
import { dropOf, dropValue } from "../drag/targets.ts";
import { COMBAT_PERCENT, combatNote, dropHow, halvedAgainstPlayers, itemGlyph, itemName, menuHeading, movingNote, slotLabel } from "./describe.ts";
import { useBagDrag, type BagMoveActions } from "./drag.ts";
import { BagMenu, DestroyConfirm, DropConfirm, bagMenuEntries, type BagMenuAt, type BagMenuChoice } from "./menu.tsx";
import { BAG_WINDOW, bagCells, bagColumns, bagEmptyHeight, bagGridInset, bagRoom, bagWindowBox } from "./layout.ts";
import { NO_LOOK, filterCounts, isLooking, lookAtBag, type BagLook, type BagLookResult } from "./looking.ts";
import { bagTipAnchor } from "./measure.ts";
import { sortingNote } from "./sort-menu.tsx";
import type { BagSlot, BagSnapshot, BagSource } from "./store.ts";
import { BagFooter } from "./footer.tsx";
import { BagTools } from "./tools.tsx";
import type { WalletSource } from "../wallet/store.ts";
import type { WindowOrder } from "../windows/order.ts";
import { windowPlace, windowSize } from "../windows/places.ts";
import { useRaise } from "../windows/raise.ts";
import type { BarArranging } from "../bar/arranging.ts";
import { say } from "../../words.ts";

/**
 * The Inventory window (P2.8-T11, ADR-091 decision 21; the design's `inventory` card in `ItemSurfaces.jsx`, a visual
 * reference): the kit's `Window` titled "Inventory" in the right window slot, holding the 10 by 5 grid of item slots.
 * It draws only what the bag store holds, so a count changes when the server says so and never before.
 *
 * - A slot of the bag holds an item's glyph and its count above one, with the pending hatch while its use waits for
 *   the server. The cells past the bag's size are drawn disabled, as every cell is before the server has named a size.
 *   A bag that holds nothing shows the design's empty state in the grid's place and at its size.
 * - While a consumable's heal is halved (in combat with a player, the `vitals` bit `InPvpCombat`, ADR-091's amendment of
 *   2026-10-08) each consumable carries the design's mark, the percent it works at, and the design's note stands
 *   under the grid; both come and go without moving a slot.
 * - The author's controls of 2026-10-08 (ADR-091's note for P2.8-T14 of them). A click on a slot that holds an item,
 *   or Enter on it while it has focus, selects it and shows its details: its tooltip pinned, the pin drawn as the
 *   design's `selected` ring; a second click lets it go. It never uses the item. A long press pins it too and its
 *   click is swallowed. Shift and a click, or Shift+Enter, splits the stack in half, quietly doing nothing where the
 *   bag's rule refuses. A right click, the context menu key or Shift+F10 opens the context menu at the slot
 *   (`menu.tsx`): Consume for a consumable, which uses it; Split; Keep in place or Let it move; Destroy, which asks
 *   first.
 * - Keep in place (P2.8-T49, ADR-125 decision 6): a stack the server holds kept draws the kit's lock in its top left
 *   corner, and its name says so; the lock follows the stack when the server's `bagKept` names its new slot.
 * - Rearranging (P2.8-T14, ADR-091's note of that row): dragging a slot picks it up (`drag.ts`), as does Ctrl+Enter on
 *   a focused slot; while a slot is picked up a click, Enter or Ctrl+Enter on any slot of the bag places it there, a
 *   click on the slot itself puts it back, the empty slots of the bag join the tab order, and Escape puts it back
 *   (`keys.ts`). A move sent hatches both its slots until the server answers; one that has waited
 *   `PENDING_SPINNER_DELAY_MS` with no answer stands the kit's pending spinner under the grid, named "Moving the
 *   <item>: waiting for the server." for a screen reader, until the answer (P2.8-T41, the author's words of
 *   2026-10-09 in ADR-091's note of that row); a dragged item's ghost follows the pointer, and the slot under it
 *   takes the design's positive ring and the ghost its positive marker where the move would be sent, the danger ones
 *   where it would be put back.
 * - The item's tooltip is the HUD's one tooltip (`bar/tooltip-view.tsx`), shown on hover, keyboard focus and long
 *   press through the page's `TipController`, which each occupied slot is an anchor of (`bag:<slot>`).
 * - It is a play window (ADR-061's amendments "a surface that is not modal never takes the game's keys" and "a play
 *   window never stops play"): its layer and its keys are `keys.ts`'s. It opens taking no focus, so the keyboard
 *   stays the game's; asked to be entered (Shift+I) it focuses its close button, so Tab then walks the search field,
 *   Filter and Sort, then the occupied slots in slot order; a pointer press never leaves focus on a slot. As it
 *   closes, focus still inside it goes back to what had it, or leaves for the page.
 * - Search and filter (P2.8-T45, ADR-125 decision 2; `tools.tsx`): the tool row stands above the grid, and while text
 *   or a filter is on a stack that misses is drawn dimmed in its place and one that matches is ringed in brass, and
 *   the empty slots of the bag are dimmed too; nothing moves and every slot keeps every action. The window holds the
 *   look while it is open and starts with none (or the page's `look`) each time it opens, so a bag never reopens with
 *   things hidden. The field hands the keyboard to the window's close button on Enter and on Escape once empty.
 * - Its content scrolls down inside a window shortened by a small area, never across; scrolling drops a tooltip
 *   whose slot moved under it.
 * - Its footer (`footer.tsx`) holds the carry number at its left (P2.8-T53, ADR-125 decision 4.3), bag slots used over
 *   the bag's size, and the wallet line at its right (P2.8-T38, ADR-124 decision 6.1, `wallet/line.tsx`), Valish coin
 *   in the coin form as the author's card draws it (ADR-125 decision 11 item 9), drawn from the page's wallet store once
 *   the server has sent a `wallet`; a page with no wallet store draws the carry number alone. The carry number and each
 *   wallet mark are anchors of the same `TipController` (`carry:0`; `wallet:<n>`, P2.8-T67).
 * - It can be moved and resized, as every game window can (the kit's `PlayWindow`; P1.9-T97 and P1.9-T183, ADR-061's
 *   amendments "a window can be moved" and "every game window moves and resizes"): by its title bar and its edges
 *   with the pointer and, with its title or its grip focused, by the arrow keys; its place and its size are kept per
 *   device and drawn held inside `bagRoom` and the window's limits, a slot's pitch (25) at a time. The grid shows the
 *   columns the drawn width holds (eleven at the design's 300, `bagColumns`), centred by `bagGridInset`, and the rows that do not fit the height
 *   scroll, so a slot keeps its index and every item stays; a window never resized is its layout's.
 */

/**
 * What the window asks of the bag beyond a drag's pick, place and put back: whether a split would be sent and why the
 * bag's rule would refuse one, the split, the destroy, the drop (P2.8-T34), the sort its Sort menu chooses (P2.8-T48),
 * and keeping a stack in place or letting it move (P2.8-T49). The bag store answers all of them.
 */
export interface BagActions extends BagMoveActions {
  readonly canSplit: (slot: number) => boolean;
  readonly splitRefusal: (slot: number) => BagRefusalReason | null;
  readonly split: (slot: number) => boolean;
  readonly destroy: (slot: number, quantity: number) => boolean;
  readonly drop: (slot: number) => boolean;
  readonly sort: (by: BagSortOrder) => boolean;
  readonly keep: (slot: number, kept: boolean) => boolean;
}

/**
 * A tooltip source that shows nothing, read where the page has no tooltips.
 */
const NO_TIPS = { subscribe: (): (() => void) => () => undefined, getSnapshot: (): null => null } as const;

/**
 * Whether the bag holds nothing the window can show as an item: its size is known and every slot is empty.
 *
 * @param bag The bag.
 * @returns True for an empty bag.
 */
function isEmpty(bag: BagSnapshot): boolean {
  return bag.size !== null && bag.slots.every((slot) => slot.item === null);
}

/**
 * The grid: every cell in slot order, as many to a row as `columns` gives (eleven by default).
 *
 * @param props The `bag`, the `columns` the window's width shows (`bagColumns`) and the `inset` that centres them (`bagGridInset`), the `rules` its items are named and drawn by, the tooltip's controller (`tips`, null where
 *   nothing shows tooltips), whether a consumable's heal is halved now (`potionsHalved`), the bag's actions (`move`,
 *   null where nothing moves), `onDrop`, which a drag released over the world drops a slot by, asking first for Rare
 *   and above (P2.8-T34), `onMenu`, which opens the context menu at a slot (null where no menu opens), and what
 *   the window's search and filter `found`, null while nothing is looked for, and the bar's arranging (`arrange`), whose
 *   slots take an item dragged onto them, null where the bar takes nothing.
 * @returns The grid.
 */
function SlotGrid({ bag, columns, inset, rules, tips, potionsHalved, move, onDrop, onMenu, found, arrange }: { readonly bag: BagSnapshot; readonly columns: number; readonly inset: number; readonly rules: Rules; readonly tips: TipController | null; readonly potionsHalved: boolean; readonly move: BagActions | null; readonly onDrop: (slot: number) => boolean; readonly onMenu: ((at: BagMenuAt) => void) | null; readonly found: BagLookResult | null; readonly arrange: BarArranging | null }): ReactElement {
  const handlers = useTipHandlers(tips);
  const drag = useBagDrag(bag, move, tips, arrange, onDrop);
  const icons = useIcons();
  const cells = bagCells(bag.size, bag.slots.length, columns);
  const holding = move !== null && bag.selected !== null;
  const dragged = drag.dragging === null ? null : (bag.slots[drag.dragging]?.item ?? null);
  const shown = useSyncExternalStore(tips?.subscribe ?? NO_TIPS.subscribe, tips?.getSnapshot ?? NO_TIPS.getSnapshot, tips?.getSnapshot ?? NO_TIPS.getSnapshot);
  const detailed = shown !== null && shown.pinned && shown.anchor.surface === "bag" ? shown.anchor.index : null;

  /**
   * Answers a click on an occupied slot: a drop's click is swallowed; with a slot picked up the click places it here;
   * Shift splits the stack; otherwise the click selects the slot and pins its details, or lets them go when they are
   * pinned there, and a long press's click is swallowed. It never uses the item.
   *
   * @param event The click.
   * @param index The slot.
   */
  const clicked = (event: MouseEvent<HTMLButtonElement>, index: number): void => {
    if (drag.swallow()) {
      return;
    }
    if (holding) {
      move.place(index);
    } else if (move !== null && event.shiftKey) {
      tips?.clear();
      move.split(index);
    } else {
      tips?.click({ surface: "bag", index });
    }
  };

  /**
   * Opens the context menu at a slot holding an item that waits for no answer, while nothing is picked up.
   *
   * @param index The slot.
   * @param x Client x of the menu's corner.
   * @param y Client y of the menu's corner.
   * @returns Whether it opened.
   */
  const menuAt = (index: number, x: number, y: number): boolean => {
    const slot = bag.slots[index];
    if (onMenu === null || move === null || holding || slot === undefined || slot.item === null || slot.pending) {
      return false;
    }
    tips?.clear();
    onMenu({ slot: index, x, y });
    return true;
  };

  /**
   * The keyboard's paths (ADR-061; ADR-091's note for P2.8-T14 of the author's controls): Shift+Enter on a focused slot
   * splits it; Ctrl+Enter picks it up, or places the slot picked up on it; the context menu key and Shift+F10 open its
   * menu below it. Enter activates a button whatever the modifier, so each keydown's default is prevented before it
   * becomes a click. Enter alone is the click's.
   *
   * @param event The key.
   */
  const keyed = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (move === null || event.repeat || event.altKey || event.metaKey) {
      return;
    }
    const anchor = dropOf(event.target as Element);
    if (anchor === null) {
      return;
    }
    const filled = (bag.slots[anchor.index]?.item ?? null) !== null;
    if (event.key === "ContextMenu" || (event.key === "F10" && event.shiftKey)) {
      const box = (event.target as Element).getBoundingClientRect();
      if (menuAt(anchor.index, box.left, box.bottom)) {
        event.preventDefault();
      }
    } else if (event.key === "Enter" && event.shiftKey && !event.ctrlKey && filled && !holding) {
      event.preventDefault();
      tips?.clear();
      move.split(anchor.index);
    } else if (event.key === "Enter" && event.ctrlKey && !event.shiftKey && (filled || holding)) {
      event.preventDefault();
      if (holding) {
        move.place(anchor.index);
      } else {
        move.pick(anchor.index);
      }
    }
  };

  /**
   * A right click on a slot opens its menu at the pointer, and the browser's own menu never shows over the bag. A
   * context menu event the keyboard raised carries no point, and opens the menu below the slot. A long press by touch on
   * a tool opens nothing: it carries the tool to the belt's chip (ADR-142 decision 3b).
   *
   * @param event The context menu event.
   */
  const contexted = (event: MouseEvent<HTMLDivElement>): void => {
    event.preventDefault();
    if (drag.carrying()) {
      return;
    }
    const anchor = dropOf(event.target as Element);
    if (anchor === null) {
      return;
    }
    const keyboard = event.clientX === 0 && event.clientY === 0;
    const box = (event.target as Element).getBoundingClientRect();
    menuAt(anchor.index, keyboard ? box.left : event.clientX, keyboard ? box.bottom : event.clientY);
  };

  return (
    <div
      className="ot-bag__grid"
      data-bag="grid"
      role="group"
      aria-label={say("bag.slots")}
      {...handlers}
      onPointerDown={(event) => {
        handlers?.onPointerDown(event);
        drag.onPointerDown(event);
      }}
      onKeyDown={keyed}
      onContextMenu={contexted}
      style={{ gridTemplateColumns: `repeat(${columns}, ${logical(BAG_WINDOW.slot)})`, gap: logical(BAG_WINDOW.gap), marginLeft: logical(inset) }}
    >
      {Array.from({ length: cells }, (_, index) => {
        const slot: BagSlot | undefined = bag.slots[index];
        const drop = dropValue({ surface: "bag", index });
        if (slot === undefined || slot.item === null) {
          const outside = bag.size === null || index >= bag.size;
          if (outside) {
            return <ItemSlot key={index} label={say("bag.lockedSlot")} disabled tabStop={false} />;
          }
          return <ItemSlot key={index} label={say("bag.emptySlot")} tabStop={holding} dimmed={found !== null} drop={drop} target={drag.over?.index === index ? drag.over.tone : null} pending={slot?.pending === true} onClick={() => (holding ? move.place(index) : undefined)} />;
        }
        return (
          <ItemSlot
            key={index}
            label={slotLabel(rules, slot.item, slot.quantity, bag.kept.includes(index))}
            rarity={rules.items.get(slot.item)?.rarity ?? null}
            art={itemArt(icons, rules, slot.item, ITEM_SLOT_ART_BOX)}
            glyph={itemGlyph(rules, slot.item)}
            count={slot.quantity}
            pending={slot.pending}
            selected={bag.selected === index || detailed === index}
            dimmed={found !== null && found.matches[index] !== true}
            matched={found !== null && found.matches[index] === true}
            kept={bag.kept.includes(index)}
            grip={arrange?.belt?.isTool(slot.item) === true}
            mark={potionsHalved && halvedAgainstPlayers(rules, slot.item) ? String(COMBAT_PERCENT) : undefined}
            tip={bagTipAnchor(index)}
            drop={drop}
            target={drag.over?.index === index ? drag.over.tone : null}
            onClick={(event) => clicked(event, index)}
          />
        );
      })}
      {dragged === null ? null : <DragGhost host={drag.ghost} label={itemName(rules, dragged)} rarity={rules.items.get(dragged)?.rarity ?? null} art={itemArt(icons, rules, dragged, ITEM_SLOT_ART_BOX)} glyph={itemGlyph(rules, dragged)} tone={drag.over?.tone ?? null} />}
    </div>
  );
}

/**
 * The spinner's name under the grid while the latest move, or the sort (P2.8-T48), has waited
 * `PENDING_SPINNER_DELAY_MS` with no answer, or null while neither is slow.
 *
 * @param bag The bag.
 * @param rules The rules its items are named by.
 * @returns The sentence a screen reader reads for the spinner.
 */
function slowMoveName(bag: BagSnapshot, rules: Rules): string | null {
  if (bag.sort.pending !== null && bag.sort.slow) {
    return sortingNote(bag.sort.pending);
  }
  const item = bag.moving === null || !bag.slow ? null : (bag.slots[bag.moving]?.item ?? null);
  return item === null ? null : movingNote(itemName(rules, item));
}

/**
 * The window while open: its box, its focus and its content.
 *
 * @param props The `area` it is laid out on, the `bag`, the `rules`, the `tips` controller, `takeEntry`, which
 *   answers once whether the window was asked to take the focus (absent where nothing asks), whether a
 *   consumable's heal is halved now (`potionsHalved`), where its place and its size are kept (`placed`, `sized`), the play windows' `order` on a page with more than
 *   one, the bag's `move` actions (null where nothing moves), `onDrop`, which a drag released over the world drops a
 *   slot by (P2.8-T34), `onMenu`, which opens the context menu at a slot (null
 *   where none opens), the page's input router (`input`, absent where no menu opens), the `look` it opens with, the
 *   wallet's `wallet` source its footer draws (null for no wallet line), the bar's arranging (`arrange`, null where the bar
 *   takes nothing), and `onClose`.
 * @returns The window in its place.
 */
function OpenBag({ area, bag, rules, tips, takeEntry, potionsHalved, placed, sized, order, move, onDrop, onMenu, input, opening, wallet, arrange, onClose }: { readonly arrange: BarArranging | null; readonly wallet: WalletSource | null; readonly input: KitInput | undefined; readonly opening: BagLook; readonly placed: PlaceSource; readonly sized: SizeSource; readonly order: WindowOrder | undefined; readonly area: HudArea; readonly bag: BagSnapshot; readonly rules: Rules; readonly tips: TipController | null; readonly takeEntry: (() => boolean) | undefined; readonly potionsHalved: boolean; readonly move: BagActions | null; readonly onDrop: (slot: number) => boolean; readonly onMenu: ((at: BagMenuAt) => void) | null; readonly onClose: () => void }): ReactElement {
  const host = useRef<HTMLDivElement>(null);
  const front = useRaise(host, order, "inventory");
  const [look, setLook] = useState(opening);
  const found = useMemo(() => lookAtBag(rules, bag.slots, look), [rules, bag.slots, look]);
  const counts = useMemo(() => filterCounts(rules, bag.slots), [rules, bag.slots]);

  /**
   * Hands the keyboard from the search field to the window: its close button takes focus, as when the window is
   * entered, so the next Escape closes it.
   */
  const leaveField = (): void => {
    host.current?.querySelector<HTMLElement>("[data-kit='window-header'] button")?.focus();
  };

  useLayoutEffect(() => {
    const box = host.current;
    const opener = document.activeElement;
    return () => {
      if (box !== null && box.contains(document.activeElement)) {
        if (opener instanceof HTMLElement && opener !== document.body && opener.isConnected && !box.contains(opener)) {
          opener.focus();
        } else {
          (document.activeElement as HTMLElement).blur();
        }
      }
    };
  }, []);

  useLayoutEffect(() => {
    if (takeEntry?.() === true) {
      host.current?.querySelector<HTMLElement>("[data-kit='window-header'] button")?.focus();
    }
  }, [bag, takeEntry]);

  useLayoutEffect(() => {
    const box = host.current;
    if (box === null || tips === null) {
      return undefined;
    }
    /**
     * Drops a bag slot's tooltip as the window's content scrolls under it.
     */
    const scrolled = (): void => {
      if (tips.getSnapshot()?.anchor.surface === "bag") {
        tips.clear();
      }
    };
    box.addEventListener("scroll", scrolled, true);
    return () => box.removeEventListener("scroll", scrolled, true);
  }, [tips]);

  const slow = slowMoveName(bag, rules);
  return (
    <PlayWindow id="inventory" title={say("bag.title")} footer={(drawn) => <BagFooter bag={bag} wallet={wallet} width={drawn.width} tips={tips} />} onClose={onClose} box={bagWindowBox(area)} room={bagRoom(area)} place={placed} size={sized} host={host} className={front ? "ot-bag is-front" : "ot-bag"} data={{ "data-bag": "window" }} padding={BAG_WINDOW.padding}>
      {(drawn) => (
        <>
          <BagTools look={look} onLook={setLook} stacks={found.stacks} hits={found.hits} counts={counts} sort={bag.sort} onSort={move === null ? null : (by) => void move.sort(by)} input={input} onLeave={leaveField} />
          {isEmpty(bag) ? (
            <div className="ot-bag__empty" data-bag="empty" style={{ height: logical(bagEmptyHeight(drawn.height)) }}>
              <EmptyState glyph="briefcase" title={say("bag.emptyTitle")}>
                {say("bag.emptyText")}
              </EmptyState>
            </div>
          ) : (
            <>
              <SlotGrid bag={bag} columns={bagColumns(drawn.width)} inset={bagGridInset(drawn.width)} rules={rules} tips={tips} potionsHalved={potionsHalved} move={move} onDrop={onDrop} onMenu={onMenu} found={isLooking(look) ? found : null} arrange={arrange} />
              {slow === null ? null : (
                <div className="ot-bag__moving" data-bag="moving" style={{ marginTop: logical(BAG_WINDOW.noteGap) }}>
                  <PendingSpinner label={slow} />
                </div>
              )}
              {potionsHalved ? (
                <p className="ot-bag__combat" data-bag="combat" style={{ marginTop: logical(BAG_WINDOW.noteGap), columnGap: logical(BAG_WINDOW.noteGlyphGap) }}>
                  <Glyph name="zap" />
                  {combatNote()}
                </p>
              ) : null}
            </>
          )}
        </>
      )}
    </PlayWindow>
  );
}

/**
 * What the HUD needs to draw the window: the bag's `source`; the `rules` its items are named and drawn by;
 * `takeEntry`, when the page has keys, which answers once whether the window was asked to take the focus; `onUse`,
 * called with the slot the context menu's Consume chose; `move`, the bag's pick, place, put back, split and destroy
 * (absent where nothing moves, P2.8-T14); the page's input router (`input`), which the context menu and the Destroy
 * confirm hold the keyboard through, absent where no menu opens; `onClose`, which the close button calls; the play
 * windows' `order`, on a page with more than one, by which the window pressed last is drawn in front; where the
 * window's place and its size are kept (`place`, `size`), the page's own for Inventory unless given; the page's `wallet` store, which the
 * footer's wallet line draws (P2.8-T38), absent where the window has no footer; and the search and filter the window
 * opens with (`look`), none unless given, which the gallery's states set; and the bar's arranging (`arrange`, P2.8-T29),
 * whose slots take an item dragged onto them, and whose placing the item menu's Place on bar starts, absent where the
 * bar takes nothing.
 */
export interface HudBag {
  readonly source: BagSource;
  readonly rules: Rules;
  readonly takeEntry?: (() => boolean) | undefined;
  readonly onUse: (slot: number) => void;
  readonly move?: BagActions | undefined;
  readonly input?: KitInput | undefined;
  readonly onClose: () => void;
  readonly onToggle?: (() => void) | undefined;
  readonly place?: PlaceSource | undefined;
  readonly size?: SizeSource | undefined;
  readonly order?: WindowOrder | undefined;
  readonly look?: BagLook | undefined;
  readonly wallet?: WalletSource | undefined;
  readonly arrange?: BarArranging | undefined;
}

/**
 * A confirm waiting for an answer: whether it asks before a destroy or a drop (P2.8-T34), the slot, and the item and
 * count it held when it was asked.
 */
interface Confirming {
  readonly kind: "destroy" | "drop";
  readonly slot: number;
  readonly item: string;
  readonly quantity: number;
}

/**
 * The Inventory window, shown while the bag store says it is open, with its context menu, its Destroy confirm and its
 * Drop confirm for Rare and above (P2.8-T34). The
 * menu and the confirm are drawn beside the window, on the HUD's layer, so the confirm's scrim covers the screen; both
 * close as the window does, and as the slot they are for empties or waits for an answer. A confirm whose slot no
 * longer holds what it named sends nothing.
 *
 * @param props The `area` it is laid out on, what the HUD hands it (`bag`), the tooltip's controller (`tips`, null
 *   where nothing shows tooltips), and whether a consumable's heal is halved now (`potionsHalved`, false unless given).
 * @returns The window, or nothing while closed.
 * @example
 * <BagWindow area={layout} bag={{ source: bag, rules: PAGE_RULES, takeEntry: () => bag.takeEntry(), onUse: (slot) => bag.use(slot), move: bag, input, onClose: () => bag.setOpen(false) }} tips={tips} />
 */
export function BagWindow({ area, bag, tips, potionsHalved = false }: { readonly area: HudArea; readonly bag: HudBag; readonly tips: TipController | null; readonly potionsHalved?: boolean }): ReactElement | null {
  const snapshot = useSyncExternalStore(bag.source.subscribe, bag.source.getSnapshot, bag.source.getSnapshot);
  const [menu, setMenu] = useState<BagMenuAt | null>(null);
  const [destroying, setDestroying] = useState<Confirming | null>(null);
  const move = bag.move ?? null;
  const menuSlot = menu === null ? undefined : snapshot.slots[menu.slot];
  const menuItem = menuSlot === undefined || menuSlot.pending ? null : menuSlot.item;

  useEffect(() => {
    if (menu === null) {
      return undefined;
    }
    /**
     * Closes the menu on a press anywhere outside it, which then does what it does there: the menu holds the keyboard,
     * so a player who clicks away gets the game's keys back at once.
     *
     * @param event The press.
     */
    const pressed = (event: globalThis.PointerEvent): void => {
      if (!(event.target instanceof Element) || event.target.closest("[data-bag='menu']") === null) {
        setMenu(null);
      }
    };
    window.addEventListener("pointerdown", pressed, true);
    return () => window.removeEventListener("pointerdown", pressed, true);
  }, [menu]);

  useEffect(() => {
    if (!snapshot.open) {
      setMenu(null);
      setDestroying(null);
    } else if (menu !== null && menuItem === null) {
      setMenu(null);
    }
  }, [snapshot.open, menu, menuItem]);

  /**
   * Does what the menu's entry says, the menu closing first so focus goes back where it was.
   *
   * @param choice The entry chosen.
   */
  const chose = (choice: BagMenuChoice): void => {
    const at = menu;
    setMenu(null);
    const slot = at === null ? undefined : snapshot.slots[at.slot];
    if (at === null || move === null || slot === undefined || slot.item === null) {
      return;
    }
    if (choice === "consume") {
      bag.onUse(at.slot);
    } else if (choice === "place") {
      bag.arrange?.startPlacingItem(slot.item, () => undefined);
    } else if (choice === "belt") {
      bag.arrange?.belt?.putOn(slot.item);
    } else if (choice === "split") {
      move.split(at.slot);
    } else if (choice === "keep") {
      move.keep(at.slot, !snapshot.kept.includes(at.slot));
    } else if (choice === "drop") {
      askDrop(at.slot);
    } else {
      setDestroying({ kind: "destroy", slot: at.slot, item: slot.item, quantity: slot.quantity });
    }
  };

  /**
   * Drops a slot's stack, by the item menu's Drop or a drag released over the world: at once for Common and Uncommon,
   * and after the Drop confirm for Rare and above (the author's ruling of 2026-10-10 for P2.8-T34); refused, the slot
   * put back and nothing sent, for Rare and above where no confirm can open (`dropHow`).
   *
   * @param at The slot.
   * @returns Whether a drop was sent or the confirm opened.
   */
  const askDrop = (at: number): boolean => {
    const slot = snapshot.slots[at];
    if (move === null || slot === undefined || slot.item === null || slot.pending) {
      return false;
    }
    const how = dropHow(bag.rules.items.get(slot.item)?.rarity ?? "common", bag.input !== undefined);
    if (how === "refuse") {
      move.putBack();
      return false;
    }
    if (how === "ask") {
      move.putBack();
      setDestroying({ kind: "drop", slot: at, item: slot.item, quantity: slot.quantity });
      return true;
    }
    return move.drop(at);
  };

  /**
   * Destroys or drops the stack the confirm named, when its slot still holds it.
   */
  const confirmed = (): void => {
    const asked = destroying;
    setDestroying(null);
    const slot = asked === null ? undefined : snapshot.slots[asked.slot];
    if (asked !== null && move !== null && slot !== undefined && slot.item === asked.item && slot.quantity === asked.quantity) {
      if (asked.kind === "drop") {
        move.drop(asked.slot);
      } else {
        move.destroy(asked.slot, asked.quantity);
      }
    }
  };

  if (!snapshot.open) {
    return null;
  }
  const onMenu = bag.input === undefined || move === null ? null : setMenu;
  return (
    <>
      <OpenBag area={area} bag={snapshot} rules={bag.rules} tips={tips} takeEntry={bag.takeEntry} potionsHalved={potionsHalved} placed={bag.place ?? windowPlace("inventory")} sized={bag.size ?? windowSize("inventory")} order={bag.order} move={move} onDrop={askDrop} onMenu={onMenu} input={bag.input} opening={bag.look ?? NO_LOOK} wallet={bag.wallet ?? null} arrange={bag.arrange ?? null} onClose={bag.onClose} />
      {bag.input === undefined || (menu === null && destroying === null) ? null : (
        <KitInputProvider input={bag.input}>
          {menu === null || menuItem === null ? null : <BagMenu key={`${menu.slot}:${menu.x}:${menu.y}`} at={menu} heading={menuHeading(itemName(bag.rules, menuItem), menuSlot?.quantity ?? 1)} entries={bagMenuEntries(bag.rules, { item: menuItem, splitRefusal: move?.splitRefusal(menu.slot) ?? null, canSplit: move?.canSplit(menu.slot) === true, kept: snapshot.kept.includes(menu.slot), placeable: bag.arrange !== undefined && bag.arrange.canArrange() && bag.arrange.placeable(menuItem), beltable: bag.arrange?.belt != null && bag.arrange.belt.canArrange() && bag.arrange.belt.isTool(menuItem) })} onSelect={chose} onClose={() => setMenu(null)} />}
          {destroying === null ? null : (
            <div className="ot-bag-confirm" data-bag="confirm">
              {destroying.kind === "drop" ? (
                <DropConfirm rules={bag.rules} slot={{ item: destroying.item, quantity: destroying.quantity, pending: false }} onConfirm={confirmed} onCancel={() => setDestroying(null)} />
              ) : (
                <DestroyConfirm rules={bag.rules} slot={{ item: destroying.item, quantity: destroying.quantity, pending: false }} onConfirm={confirmed} onCancel={() => setDestroying(null)} />
              )}
            </div>
          )}
        </KitInputProvider>
      )}
    </>
  );
}
